import React from 'react';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
  ComposedChart,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  ReferenceLine,
  Label,
} from 'recharts';
import Grid from '@material-ui/core/Grid';
import Skeleton from '@material-ui/lab/Skeleton';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Switch from "@material-ui/core/Switch";
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import BaseWeekStoreDashboardView from '../BaseWeekStoreDashboardView';
import Header from '../../components/utils/Header';
import FilterListView from '../../components/FilterListView';
import { formatFloat, unitFormatter } from '../../utils';
import DownloadButton from '../../components/utils/DownloadButton';
import { athenaRed } from '../../globals';
import DataFrame from "../../components/utils/DataFrame";

@observer
class WeeklyDemandView extends BaseWeekStoreDashboardView {
  static apiSubPath = '/demand/';

  static units = {
    TO: 'Tons',
    KPC: 'Pieces',
    L: 'Liters',
    kEUR: 'Euros',
  };

  static previousVersions = {
    1: 'W-1',
    4: 'W-4',
  };

  @observable unit = Object.keys(this.constructor.units)[0];
  @observable previousVersion = Object.keys(this.constructor.previousVersions)[0];
  @observable weeklyMode = true;
  @observable actualsLevel = 'W-6';
  @observable forecastsLevel = 'W+38';

  _setData(data) {
    if ('week_store' in data && data.week_store) {
      this.weekStoreId = parseInt(data.week_store);
    }
    this.weeklyMode = data.weekly_mode === null ? true : data.weekly_mode;
  }

  _getQueryParams() {
    return {
        weekly_mode: this.weeklyMode,
    };
  }

  stockFormatter = unitFormatter('t', 1);
  kPiecesFormatter = unitFormatter('k', 1);
  literFormatter = unitFormatter('L', 1);
  euroFormatter = unitFormatter('k€', 1);

  tooltipFormatter = (value, name) => {
    if (name.endsWith(' (Tons)')) {
      return this.stockFormatter(value);
    }
    if (name.endsWith(' (Pieces)')) {
      return this.kPiecesFormatter(value);
    }
    if (name.endsWith(' (Liters)')) {
      return this.literFormatter(value);
    }
    if (name.endsWith(' (Euros)')) {
      return this.euroFormatter(value);
    }
    return formatFloat(value, 0);
  };

  xAxisFormatter = value => {
    value = `${value}`;
    if (this.weeklyMode) {
      return `${value.slice(0, 4)}-W${value.slice(4)}`;
    }
    return `${value.slice(0, 4)}-M${value.slice(4)}`;
  };

  yAxisFormatter = value => {
    if (this.unit === 'TO') {
      return this.stockFormatter(value);
    }
    if (this.unit === 'KPC') {
      return this.kPiecesFormatter(value);
    }
    if (this.unit === 'L') {
      return this.literFormatter(value);
    }
    if (this.unit === 'kEUR') {
      return this.euroFormatter(value);
    }
    return formatFloat(value, 0);
  };

  onUnitChange = this.genericChangeHandler('unit', false);
  onPreviousVersionChange = this.genericChangeHandler('previousVersion', false);
  
  @action onWeeklyModeChange = event => {
    this.weeklyMode = event.target.checked;
    this.updateData();
  };

  @action onActualsLevelChange = event => {
    this.actualsLevel = event.target.checked ? 'W-1' : 'W-6';
  };

  @action onForecastsLevelChange = event => {
    this.forecastsLevel = event.target.checked ? 'W+38' : 'W+16';
  };

  get controls() {
    if (!this.initialized) {
      return <Skeleton variant="rect" width={480} height={55} />;
    }
    return (
      <Grid container justify="flex-end" alignItems="center" spacing={4}>
        <Grid item>
          <Typography noWrap>
            W-6
            <Switch
              checked={this.actualsLevel === 'W-1'}
              onChange={this.onActualsLevelChange}
              disabled={!this.weeklyMode}
            />
            W-1
          </Typography>
        </Grid>
        <Grid item>
          <Typography noWrap>
            W+16
            <Switch
              checked={this.forecastsLevel === 'W+38'}
              onChange={this.onForecastsLevelChange}
              disabled={!this.weeklyMode}
            />
            W+38
          </Typography>
        </Grid>
        <Grid item>
          <Typography noWrap>
            Monthly
            <Switch
              checked={this.weeklyMode}
              onChange={this.onWeeklyModeChange}
            />
            Weekly
          </Typography>
        </Grid>
        <Grid item>
          {this.weekStoreField}
        </Grid>
        <Grid item>
          <TextField variant="outlined" select label="Compare to" value={this.previousVersion} onChange={this.onPreviousVersionChange}>
            {Object.entries(this.constructor.previousVersions).map(([value, name]) =>
              <MenuItem key={value} value={value}>{name}</MenuItem>
            )}
          </TextField>
        </Grid>
        <Grid item>
          <TextField variant="outlined" select label="Unit" value={this.unit} onChange={this.onUnitChange}>
            {Object.entries(this.constructor.units).map(([value, name]) =>
              <MenuItem key={value} value={value}>{name}</MenuItem>
            )}
          </TextField>
        </Grid>
        <Grid item>
          <FilterListView filterList={this.filterList} />
        </Grid>
        <Grid item>
          <DownloadButton
            href={this.weekStore.getApiUrl('/demand/?' + this.getQueryString({ export: 'xlsx' }))}
            permission="parthenos.download_demand"
          />
        </Grid>
      </Grid>
    );
  }

  @computed get graphData() {
    let data = [...this.data.graph_data];
    if (! this.weeklyMode){
      return data
    }
    if (this.actualsLevel === 'W-1') {
      data = data.slice(5);
    }
    if (this.forecastsLevel === 'W+16') {
      data = data.slice(0, data.length-10);
    }
    return data;
  }

  @computed get timeCode() {
    if (this.weeklyMode) {
      return {dataKey: "WEK_COD", name: "week"} 
    }
    else {
      return {dataKey: "MTH_COD", name: "month"}
    }
  }

  get chart() {
    if (!this.ready) {
      return <Skeleton variant="rect" height="100%" />;
    }
    const actualsEndWeek = (this.data.graph_data.length >= 6 && this.weeklyMode)? this.data.graph_data[5].WEK_COD : null;
    const actualsEndMonth = (this.data.current_month_code && !this.weeklyMode)? this.data.current_month_code : null;
    const addReferenceLine = (actualsEnd) => actualsEnd && (
      <ReferenceLine
        x={actualsEnd}
        position="end"
        label={<Label position="top" fill={athenaRed}>Actuals end</Label>}
        strokeDasharray="3 3"
        stroke={athenaRed}
        isFront
      />
    )
    // console.log(actualsEndMonth, actualsEndWeek)

    return (
      <ComposedChart data={this.graphData} margin={{ top: 16, right: 16, bottom: 16, left: 32 }}>
        <XAxis dataKey={this.timeCode.dataKey} name={this.timeCode.name} tickFormatter={this.xAxisFormatter} />
        <YAxis tickFormatter={this.yAxisFormatter} />
        <Legend onClick={this.onLegendClick} formatter={this.legendFormatter} />
        <RechartsTooltip formatter={this.tooltipFormatter} />
        {!this.weeklyMode && this.getBar(`actuals_demand_${this.unit}`, { fill: '#4FA5F0', name: `Demand actuals (${this.unit})`, stackId: "demand" })}
        {this.getBar(`base_demand_${this.unit}`, { fill: '#26d0fe', name: `Demand (${this.unit})`, stackId: "demand" })}
        {this.getBar(`demand_in_past_${this.unit}`, { fill: '#a11fc4', name: `Demand in the past (${this.unit})`, stackId: "demand" })}
        {this.getBar(`back_orders_${this.unit}`, { fill: '#0194ba', name: `Back orders (${this.unit})`, stackId: "demand" })}
        {this.getBar(`sales_orders_${this.unit}`, { fill: '#7A91EB', name: `Sales orders (${this.unit})`, stackId: "demand" })}
        {this.getBar(`W_minus_${this.previousVersion}_total_demand_${this.unit}`, { fill: '#98c1f0', name: `Previous demand (W-${this.previousVersion}) (${this.unit})`, stackId: "version" })}
        {this.getBar(`horizon_${this.previousVersion}_total_demand_${this.unit}`, { fill: '#97b0f0', name: `Past forecasts (W-${this.previousVersion} horizon) (${this.unit})`, stackId: "version" })}
        {addReferenceLine(actualsEndWeek)}
        {addReferenceLine(actualsEndMonth)}
      </ComposedChart>
    );
  }

  formatTableColumn = (serializedDataFrame, columnsRowIndex, columnName, columnIndex) => {
    columnName = `${columnName}`;
    if (this.weeklyMode) {
      return `${columnName.slice(0, 4)}-W${columnName.slice(4)}`;
    }
    return `${columnName.slice(0, 4)}-M${columnName.slice(4)}`;
  };

  formatTableData = (serializedDataFrame, columnIndex, rowIndex, value) => formatFloat(value, 1);

  get MarketComparisonTable() {
    if (!this.ready) {
      return <Skeleton variant="rect" height={600} />;
    }
    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{`Market Comparison Table (${this.unit}, W-${this.previousVersion})`}</Typography>
        </AccordionSummary>
        <AccordionDetails overflow='auto'>
          <DataFrame
            serializedDataFrame={this.data.cbu_table_data[`${this.unit}_W-${this.previousVersion}`]}            
            formatColumn={this.formatTableColumn}
            formatData={this.formatTableData}
            maxHeight={600}
          />
        </AccordionDetails>
      </Accordion>
    );
  }

  get stageTable() {
    if (!this.ready) {
      return <Skeleton variant="rect" height={600} />;
    }
    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{`Stage Demand Table (${this.unit})`}</Typography>
        </AccordionSummary>
        <AccordionDetails overflow='auto'>
          <DataFrame
            serializedDataFrame={this.data.stage_table_data[this.unit]}
            formatColumn={this.formatTableColumn}
            formatData={this.formatTableData}
            maxHeight={600}
          />
        </AccordionDetails>
      </Accordion>
    );
  }

  get technologyMarketTable() {
    if (!this.ready) {
      return <Skeleton variant="rect" height={600} />;
    }
    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{`Technology x Market Demand Table (${this.unit})`}</Typography>
        </AccordionSummary>
        <AccordionDetails overflow='auto'>
          <DataFrame
            serializedDataFrame={this.data.technology_cbu_table_data[this.unit]}
            formatColumn={this.formatTableColumn}
            formatData={this.formatTableData}
            maxHeight={600}
          />
        </AccordionDetails>
      </Accordion>
    );
  }

  render() {
    return (
      <Grid container direction="column" wrap="nowrap" spacing={4}>
        <Grid item>
          <Grid container justify="space-between" alignItems="center" spacing={1} wrap="nowrap">
            <Grid item><Header title="Demand" /></Grid>
            <Grid item>{this.controls}</Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Box overflow="hidden">
            <Paper>
              <ResponsiveContainer aspect={21 / 9}>{this.chart}</ResponsiveContainer>
            </Paper>
          </Box>
        </Grid>
        <Grid item>
          {this.MarketComparisonTable}
          {this.stageTable}
          {this.technologyMarketTable}
        </Grid>
      </Grid>
    );
  }
}


export default WeeklyDemandView;
